import { useId, useState } from 'react';
import type { GlossaryTerm, Ingredient } from '../data/types';
import { scaleAmount } from '../lib/scale';

// Renders step text, resolving two kinds of inline markup:
//
//   {1 tablespoon|soy}  an amount that scales with the rest of the recipe and
//                       links to that ingredient's row in the ledger
//   {1 cup}             an amount that scales but links nowhere
//
// Everything else is left alone on purpose. Times, temperatures, pan counts and
// "¼-inch cubes" do not change when the batch size does — only quantities do.
// Glossary terms in the remaining prose get a dotted underline and a
// tap-to-open plain-English definition, inline so nothing overlaps on a phone.

const AMOUNT = /\{([^{}|]+?)(?:\|([a-z0-9-]+))?\}/g;

type Piece =
  | { kind: 'text'; text: string }
  | { kind: 'term'; text: string; term: GlossaryTerm }
  | { kind: 'amount'; text: string; approx: boolean; id?: string; name?: string };

function splitByTerms(body: string, glossary: GlossaryTerm[]): Piece[] {
  if (!body) return [];
  if (!glossary.length) return [{ kind: 'text', text: body }];
  const sorted = [...glossary].sort((a, b) => b.term.length - a.term.length);
  const pattern = sorted.map((g) => g.term.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')).join('|');
  const re = new RegExp(`(${pattern})`, 'gi');
  const pieces: Piece[] = [];
  let last = 0;
  for (const m of body.matchAll(re)) {
    if (m.index! > last) pieces.push({ kind: 'text', text: body.slice(last, m.index) });
    const term = sorted.find((g) => g.term.toLowerCase() === m[1].toLowerCase());
    if (term) pieces.push({ kind: 'term', text: m[1], term });
    else pieces.push({ kind: 'text', text: m[1] });
    last = m.index! + m[1].length;
  }
  if (last < body.length) pieces.push({ kind: 'text', text: body.slice(last) });
  return pieces;
}

function parse(
  body: string,
  glossary: GlossaryTerm[],
  ingredients: Ingredient[],
  factor: number
): Piece[] {
  const out: Piece[] = [];
  let last = 0;
  for (const m of body.matchAll(AMOUNT)) {
    if (m.index! > last) out.push(...splitByTerms(body.slice(last, m.index), glossary));
    const { text, approx } = scaleAmount(m[1], factor);
    const ing = m[2] ? ingredients.find((i) => i.id === m[2]) : undefined;
    out.push({ kind: 'amount', text, approx, id: ing?.id, name: ing?.name });
    last = m.index! + m[0].length;
  }
  if (last < body.length) out.push(...splitByTerms(body.slice(last), glossary));
  return out;
}

export function GlossaryText({
  body,
  glossary,
  ingredients = [],
  factor = 1,
  accent,
  linkIngredients = true,
  className = '',
}: {
  body: string;
  glossary: GlossaryTerm[];
  ingredients?: Ingredient[];
  factor?: number;
  accent?: string;
  linkIngredients?: boolean;
  className?: string;
}) {
  const [open, setOpen] = useState<number | null>(null);
  const idBase = useId();
  const pieces = parse(body, glossary, ingredients, factor);
  const openPiece = open !== null ? pieces[open] : null;

  return (
    <div className={className}>
      <p className="font-serif leading-relaxed">
        {pieces.map((p, i) => {
          if (p.kind === 'amount') {
            const inner = (
              <>
                {p.approx && <span className="font-normal text-ink-soft">≈</span>}
                {p.text}
              </>
            );
            if (p.id && linkIngredients) {
              return (
                <a
                  key={i}
                  href={`#ing-${p.id}`}
                  title={p.name ? `${p.name} — jump to ingredients` : undefined}
                  style={accent ? { color: accent } : undefined}
                  className="font-semibold underline decoration-dotted underline-offset-4 hover:decoration-solid focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ink print:no-underline"
                >
                  {inner}
                </a>
              );
            }
            return (
              <strong key={i} className="font-semibold">
                {inner}
              </strong>
            );
          }
          if (p.kind === 'term') {
            return (
              <button
                key={i}
                type="button"
                aria-expanded={open === i}
                aria-controls={open === i ? `${idBase}-def` : undefined}
                onClick={() => setOpen(open === i ? null : i)}
                className="cursor-pointer rounded-sm underline decoration-dotted decoration-ink-soft underline-offset-4 hover:decoration-ink focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ink"
              >
                {p.text}
              </button>
            );
          }
          return <span key={i}>{p.text}</span>;
        })}
      </p>
      {openPiece?.kind === 'term' && (
        <p
          id={`${idBase}-def`}
          className="mt-2 rounded-lg bg-line/40 px-3.5 py-2.5 font-serif text-[0.92rem] leading-relaxed text-ink"
        >
          <span className="font-sans text-[0.72rem] font-semibold uppercase tracking-[0.08em] text-ink-soft">
            {openPiece.term.term}
          </span>
          <span className="block">{openPiece.term.plain}</span>
        </p>
      )}
    </div>
  );
}
